/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { VerifyPhoneViewProps } from '../../widgets/VerifyPhoneView/types';
import { Purpose, type PhoneVerificationChannel } from '../../managers/sso';
import type { OtpFlow, VerifyPhoneOtpFlowResult } from './types';
import { type PhoneFlowKey, PHONE_OTP_PURPOSE_CONFIG } from './purposeConfig';
import type { PostVerificationProfileFields } from '../../utils/verifyPhoneOtpUtils';
import { useUser } from '../account';
import { useEmailChangeOnPhoneOtpFlow } from './phone/useEmailChangeOnPhoneOtpFlow';
import { useLogin2FAOtpFlow } from './phone/useLogin2FAOtpFlow';
import { usePhoneChangeOtpFlow } from './phone/usePhoneChangeOtpFlow';
import { usePhoneVerificationOtpFlow } from './phone/usePhoneVerificationOtpFlow';

export type UseVerifyPhoneOtpFlowParams = Pick<
  VerifyPhoneViewProps,
  'purpose' | 'phoneNumber' | 'dialingCode' | 'availableChannels' | 'onVerified'
> & {
  startTimer: () => void;
  profileUpdate?: PostVerificationProfileFields;
};

export function useVerifyPhoneOtpFlow({
  purpose = Purpose.USERNAME_CHANGE,
  phoneNumber,
  dialingCode,
  availableChannels,
  profileUpdate,
  startTimer,
  onVerified,
}: UseVerifyPhoneOtpFlowParams): VerifyPhoneOtpFlowResult {
  const { data: user } = useUser('VerifyPhoneView');

  const displayPhoneNumber = phoneNumber ?? user?.phoneNumber;
  const displayDialingCode = dialingCode ?? user?.dialingCode;
  const resolvedChannels: PhoneVerificationChannel[] = availableChannels ?? [];

  const phoneVerificationFlow = usePhoneVerificationOtpFlow({
    displayPhoneNumber,
    displayDialingCode,
    resolvedChannels,
    cultureCode: user?.cultureCode,
    profileUpdate,
    startTimer,
    onVerified,
  });

  const login2faFlow = useLogin2FAOtpFlow({
    displayPhoneNumber,
    displayDialingCode,
    startTimer,
    onVerified,
  });

  const phoneChangeFlow = usePhoneChangeOtpFlow({
    displayPhoneNumber,
    displayDialingCode,
    startTimer,
    onVerified,
  });

  const emailChangeFlow = useEmailChangeOnPhoneOtpFlow({
    startTimer,
    onVerified,
  });

  const workers: Record<PhoneFlowKey, OtpFlow> = {
    phoneVerification: phoneVerificationFlow,
    login2fa: login2faFlow,
    phoneChange: phoneChangeFlow,
    emailChangeOnPhone: emailChangeFlow,
  };

  const { worker, behaviour } =
    PHONE_OTP_PURPOSE_CONFIG[purpose] ??
    PHONE_OTP_PURPOSE_CONFIG[Purpose.USERNAME_CHANGE];

  return {
    ...workers[worker],
    behaviour,
    displayPhoneNumber,
    displayDialingCode,
    resolvedChannels,
    requestOtpViaChannel: phoneVerificationFlow.requestOtpViaChannel,
  };
}
